import { Field } from 'react-final-form';
import styled, { css } from 'styled-components/macro';
import * as validators from 'validators';
import { CalculatorFormValues, StandType } from 'common/types';
import { FC, ReactNode, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { combineValidators } from 'validators/combineValidators';
import * as formFormatters from '@net0-calc/common/form-utils/formatters';
import * as formParsers from '@net0-calc/common/form-utils/parsers';
import {
  BasicStandIcon,
  BoxIcon,
  CheckIcon,
  CreativeStandIcon,
  EcoFriendlyIcon,
  PeopleIcon,
  RecycleIcon,
  RegularStandIcon,
  ShoppingIcon,
} from '@net0-calc/common/assets/svg';
import { DetailedFormSwitch } from '@net0-calc/common/components';
import { TimeUtil } from '@net0-calc/common/utils';
import {
  Hints,
  Input,
  RadioButton,
  CardRadioButton,
  StepDescription,
  StepTitle,
} from '@net0-calc/common/components/ui';
import { DEVICE, TYPOGRAPHY } from '@net0-calc/common/assets/styles';
import { WizardPageProps } from '@net0-calc/common/types';

const LABEL_CSS = css`
  ${TYPOGRAPHY.bodyRegular16}
`;

const DETAILED_FIELDS_CONFIG: { name: string; icon: ReactNode; unit: string }[] = [
  { name: 'tShirtsCount', icon: <ShoppingIcon stroke="var(--color1)" />, unit: 'pcs' },
  { name: 'brandedBagsCount', icon: <BoxIcon stroke="var(--color1)" />, unit: 'pcs' },
  { name: 'usedPlasticKg', icon: <RecycleIcon stroke="var(--color1)" />, unit: 'kg' },
  { name: 'usedPaperKg', icon: <BoxIcon stroke="var(--color1)" />, unit: 'kg' },
  { name: 'usedWoodKg', icon: <EcoFriendlyIcon stroke="var(--color1)" />, unit: 'kg' },
];

const ICON_WRAPPER_CSS = css<{ $isChecked?: boolean }>`
  & svg path {
    width: 60px;
    height: 60px;
    stroke: none;
    transition: fill 1s;
  }
  ${({ $isChecked }) =>
    $isChecked
      ? css`
          & svg path {
            fill: var(--color5);
          }
        `
      : css`
          & svg {
            & path {
              fill: var(--color1);
            }
          }
        `}
`;

const InputCard = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => (
  <InputCardRoot>
    <InputCardIcon>{icon}</InputCardIcon>
    <InputCardContent>{children}</InputCardContent>
  </InputCardRoot>
);

export const MerchandisePage: FC<WizardPageProps<CalculatorFormValues>> = ({ values, form }) => {
  const { t } = useTranslation();

  const detailedFields = useMemo(() => {
    return DETAILED_FIELDS_CONFIG.map(({ name, icon, unit }) => {
      return { label: t(`events.pages.merchandise.form.field.${name}.label`), name, icon, unit };
    });
  }, []);

  const options = [
    {
      value: StandType.BASIC,
      title: t(`events.pages.merchandise.form.field.standType.options.basic`),
      icon: <BasicStandIcon />,
    },
    {
      value: StandType.REGULAR,
      title: t(`events.pages.merchandise.form.field.standType.options.regular`),
      icon: <RegularStandIcon />,
    },
    {
      value: StandType.CREATIVE,
      title: t(`events.pages.merchandise.form.field.standType.options.creative`),
      icon: <CreativeStandIcon />,
    },
  ];

  const isDetailedForm = values?.isMerchandiseDetailed;
  const hasExhibitors = values?.hasExhibitors;

  useEffect(() => {
    const eventDurationDates = values?.eventDurationDates || [new Date(), new Date()];
    const eventBeginingDate = eventDurationDates[0];
    const mothsDiffBetweenNowAndEventBeginingDate = TimeUtil.getDiffFromNow(eventBeginingDate, 'months');
    const isMerchandiseDetailed = mothsDiffBetweenNowAndEventBeginingDate < 6;
    if (form) {
      form.change('isMerchandiseDetailed', isMerchandiseDetailed);
    }
  }, []);

  const hint = isDetailedForm ? t('events.pages.merchandise.hint.advanced') : t('events.pages.merchandise.hint.simple');

  return (
    <Root>
      <DetailedFormSwitch isDetailedForm={!!isDetailedForm} name="isMerchandiseDetailed" />
      <StepTitle>{t('events.pages.merchandise.title')}</StepTitle>
      <StepDescription>{t('events.pages.merchandise.subTitle')}</StepDescription>

      {isDetailedForm ? (
        <FormWrap>
          <CardsGrid>
            {detailedFields.map(({ name, label, icon, unit }) => (
              <Field
                key={name}
                name={name}
                type="number"
                pattern="[0-9]*"
                parse={formParsers.toNumber}
                format={formFormatters.numberToString}
                validate={combineValidators(validators.isNegativeNumber, validators.isNumber)}>
                {field => (
                  <InputCard icon={icon}>
                    <CardInput label={label} placeholder="0" unit={unit} {...field} />
                  </InputCard>
                )}
              </Field>
            ))}
          </CardsGrid>
        </FormWrap>
      ) : (
        <FormWrap>
          <CardsGrid $singleColumn>
            <Field
              name="promotionalBagsNumber"
              type="number"
              pattern="[0-9]*"
              parse={formParsers.toNumber}
              format={formFormatters.numberToString}
              validate={combineValidators(validators.isNegativeNumber, validators.isNumber)}>
              {field => (
                <InputCard icon={<ShoppingIcon stroke="var(--color1)" />}>
                  <CardInput
                    label={t('events.pages.merchandise.form.field.promotionalBagsNumber.label')}
                    placeholder="0"
                    unit="pcs"
                    {...field}
                  />
                </InputCard>
              )}
            </Field>
          </CardsGrid>

          <Field name="hasExhibitors" type="checkbox">
            {({ input }) => (
              <StyledRadioButton
                text={t('events.pages.merchandise.hasExhibitors')}
                textCSS={LABEL_CSS}
                icon={CheckIcon}
                {...input}
              />
            )}
          </Field>

          {hasExhibitors && (
            <>
              <CardsGrid $singleColumn>
                <Field
                  name="exhibitorNumber"
                  type="number"
                  pattern="[0-9]*"
                  parse={formParsers.toNumber}
                  format={formFormatters.numberToString}
                  validate={combineValidators(validators.isNegativeNumber, validators.isNumber)}>
                  {field => (
                    <InputCard icon={<PeopleIcon stroke="var(--color1)" />}>
                      <CardInput
                        label={t('events.pages.merchandise.form.field.exhibitorNumber.label')}
                        placeholder="0"
                        {...field}
                      />
                    </InputCard>
                  )}
                </Field>
              </CardsGrid>
              <Description>{t('events.pages.merchandise.standType')}</Description>
              <StandCardsGroup>
                {options.map(({ value, title, icon }) => (
                  <Field key={value} name="standTypes" type="checkbox" value={value}>
                    {({ input }) => (
                      <CardRadioButton
                        iconWrapperCSS={ICON_WRAPPER_CSS}
                        id={`${value}-option`}
                        icon={icon}
                        title={title}
                        {...input}
                      />
                    )}
                  </Field>
                ))}
              </StandCardsGroup>
            </>
          )}
        </FormWrap>
      )}

      <Hints hints={[hint]} />
    </Root>
  );
};

const Root = styled.div`
  width: 100%;
  position: relative;
  margin-bottom: 60px;

  @media ${DEVICE.laptop} {
    margin-bottom: 70px;
  }
`;

const FormWrap = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;

  @media ${DEVICE.laptop} {
    margin-bottom: 50px;
  }
`;

const CardsGrid = styled.div<{ $singleColumn?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;

  @media ${DEVICE.laptop} {
    grid-template-columns: ${({ $singleColumn }) => ($singleColumn ? '1fr' : 'repeat(2, 1fr)')};
    gap: 16px;
    margin-bottom: 20px;
  }
`;

const InputCardRoot = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 16px 18px;
  background: var(--color14);
  border: 1px solid var(--color14);
  border-radius: 16px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    border-color: var(--color10);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }

  &:focus-within {
    border-color: var(--color2);
    box-shadow: 0 0 0 3px rgba(206, 17, 38, 0.06);
  }

  @media ${DEVICE.laptop} {
    padding: 20px;
    gap: 16px;
  }
`;

const InputCardIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color5);
  border-radius: 12px;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  overflow: hidden;

  & svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    overflow: visible;
  }
`;

const InputCardContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardInput = styled(Input)`
  white-space: nowrap;

  /* Override label style inside card */
  & > label {
    ${TYPOGRAPHY.captionRegular12}
    color: var(--color9);
    margin-bottom: 6px;
  }

  /* Override input wrap style inside card */
  & > div {
    height: 44px;
    border-radius: 10px;
    margin-bottom: 0;
    background: var(--color5);

    @media ${DEVICE.laptop} {
      margin-bottom: 0;
    }
  }
`;

const Description = styled(StepDescription)`
  margin-bottom: 24px;

  @media ${DEVICE.laptop} {
    margin-bottom: 24px;
  }
`;

const StandCardsGroup = styled.div`
  display: grid;
  justify-content: center;
  grid-template-columns: repeat(2, 165px);
  grid-gap: 13px;
  margin-bottom: 16px;

  @media ${DEVICE.tablet} {
    justify-content: flex-start;
    grid-gap: 20px;
    grid-template-columns: repeat(auto-fit, 195px);
    margin-bottom: 20px;
  }
`;

const StyledRadioButton = styled(RadioButton)`
  margin-bottom: 20px;

  ${({ checked }) =>
    checked
      ? css`
          margin-bottom: 24px;
        `
      : css`
          margin-bottom: 0;
        `}
`;
